import Loading from '@app/statics/Loading';
import SearchIcon from '@assets/images/our-library-au.png';
import type { IStoryList } from '@coreStory/type';
import { useGetHashtagList, useGetStories } from '@coreStory/useCase';
import { Col, Flex, Pagination, Row, Tag } from 'antd';
import React, { useEffect, useState } from 'react';
import { BiSearchAlt } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';
import StoryCard from './StoryCard';
import styles from './index.module.scss';

import { ButtonCore, EmptyStory, InputCore } from '@components';
import {
  VisibilityModal,
  EditTitleModal,
} from '@app/story/ViewStory/components';
import { useUpdateStoryVisibility } from '@coreStory/useCase';

const WebLibrary = () => {
  const getUserStory = useGetStories();

  const getUserHashtag = useGetHashtagList();

  const [MyStoryHashtagAvtice, setMyStoryHashtagAvtice] = useState<any>();
  const [paginatedUserStories, setPaginatedUserStories] = useState<
    IStoryList[]
  >([]);
  const [paginatedUserPublicStories, setPaginatedUserPublicStories] = useState<
    IStoryList[]
  >([]);

  const [filteredUserStories, setFilteredUserStories] = useState<IStoryList[]>(
    [],
  );

  const navigate = useNavigate();

  // Modal states and selected stories
  const [isVisibilityModalVisible, setIsVisibilityModalVisible] =
    useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isEditTitleModalVisible, setIsEditTitleModalVisible] = useState(false);
  const [selectedVisibilityStory, setSelectedVisibilityStory] =
    useState<IStoryList | null>(null);
  const [selectedEditStory, setSelectedEditStory] = useState<IStoryList | null>(
    null,
  );
  const [selectedEditTitleStory, setSelectedEditTitleStory] =
    useState<IStoryList | null>(null);

  const { mutate: updateVisibility, isLoading: isUpdatingVisibility } =
    useUpdateStoryVisibility();

  useEffect(() => {
    getUserStory.mutate({ public: false, hashtagid: 0 });
    getUserHashtag.mutate({ public: false });
  }, []);

  useEffect(() => {
    getUserStory.data && setFilteredUserStories(getUserStory.data);
    getUserStory.data &&
      setPaginatedUserStories(
        paginateData(
          getUserStory.data.filter((item) => item.IsPrivate === true),
        ),
      );
    getUserStory.data &&
      setPaginatedUserPublicStories(
        paginateData(
          getUserStory.data.filter((item) => item.IsPrivate === false),
        ),
      );
  }, [getUserStory.isSuccess]);

  const paginateData = (
    data: IStoryList[],
    pageNo = 1,
    pageSize = 4,
  ): IStoryList[] => {
    return data.filter(
      (_, index) =>
        index >= (pageNo - 1) * pageSize && index <= pageNo * pageSize - 1,
    );
  };

  const filterData = (data: IStoryList[], filterValue = ''): IStoryList[] => {
    return data.filter(
      (story) =>
        story.StoryText.toLowerCase().trim().includes(filterValue) ||
        story.StoryName.toLowerCase().trim().includes(filterValue) ||
        story.ChildName.toLowerCase().trim().includes(filterValue),
    );
  };

  useEffect(() => {
    setPaginatedUserStories(
      paginateData(
        filteredUserStories.filter((item) => item.IsPrivate === true),
      ),
    );
    setPaginatedUserPublicStories(
      paginateData(
        filteredUserStories.filter((item) => item.IsPrivate === false),
      ),
    );
  }, [filteredUserStories]);

  const handleInput = (event: React.FormEvent<HTMLInputElement>) => {
    const text = event.currentTarget.value.toLowerCase().trim();
    if (text.length >= 3) {
      getUserStory.data &&
        setFilteredUserStories(filterData(getUserStory.data, text));
    } else {
      getUserStory.data && setFilteredUserStories(getUserStory.data);
    }
  };
  const handleMyStoryHashtag = (id: any) => {
    setMyStoryHashtagAvtice(id);
    getUserStory.mutate({ public: false, hashtagid: id });
  };
  const refreshStories = () => {
    getUserStory.mutate({
      public: false,
      hashtagid: MyStoryHashtagAvtice ?? 0,
    });
  };

  // StoryCard action handlers
  const handleCardEditTitle = (story: IStoryList) => {
    setSelectedEditTitleStory(story);
    setIsEditTitleModalVisible(true);
  };

  const handleCardSetVisibility = (story: IStoryList) => {
    setSelectedVisibilityStory(story);
    setIsVisibilityModalVisible(true);
  };

  // Modal callbacks
  const handleConfirmVisibility = (status: boolean) => {
    if (!selectedVisibilityStory) return;
    updateVisibility(
      { story_id: selectedVisibilityStory.StoryID, is_private: status },
      {
        onSuccess: () => {
          setIsVisibilityModalVisible(false);
          setSelectedVisibilityStory(null);
          refreshStories();
        },
      },
    );
  };

  const handleCancelVisibility = () => {
    setIsVisibilityModalVisible(false);
    setSelectedVisibilityStory(null);
  };

  const handleCancelEdit = () => {
    setIsEditModalVisible(false);
    setSelectedEditStory(null);
  };

  const handleEditSuccess = () => {
    setIsEditModalVisible(false);
    setSelectedEditStory(null);
    refreshStories();
  };

  const handleCancelEditTitle = () => {
    setIsEditTitleModalVisible(false);
    setSelectedEditTitleStory(null);
  };

  const handleEditTitleSuccess = () => {
    setIsEditTitleModalVisible(false);
    setSelectedEditTitleStory(null);
    refreshStories();
  };
  return (
    <>
      <div className={styles['search_box']}>
        <img src={SearchIcon} alt="search" />
        <InputCore
          placeholder="search"
          suffix={<BiSearchAlt />}
          className={styles['input']}
          onInput={(e) => handleInput(e)}
        />
      </div>
      <div id="private-stories" className={styles['story_container']}>
        <Flex className={styles['story_header']} align="center">
          <h2 className={styles['title']}>My Private Stories</h2>
          {getUserHashtag.data && (
            <Flex
              style={{
                marginLeft: 20,
                maxWidth: 'calc(100% - 150px)',
                overflowX: 'auto',
                paddingBottom: 5,
              }}>
              <Tag
                style={{
                  borderColor: '#8A8181',
                  cursor: 'pointer',
                  color: MyStoryHashtagAvtice === 0 ? '#fff' : '#8A8181',
                  borderRadius: 50,
                  background:
                    MyStoryHashtagAvtice === 0 ? '#8A8181' : 'transparent',
                }}
                onClick={() => handleMyStoryHashtag(0)}>
                #All
              </Tag>
              {getUserHashtag.data.map((item) => (
                <Tag
                  key={item.HashtagID}
                  style={{
                    borderColor: '#8A8181',
                    cursor: 'pointer',
                    color:
                      MyStoryHashtagAvtice === item.HashtagID
                        ? '#fff'
                        : '#8A8181',
                    borderRadius: 50,
                    background:
                      MyStoryHashtagAvtice === item.HashtagID
                        ? '#8A8181'
                        : 'transparent',
                  }}
                  onClick={() => handleMyStoryHashtag(item.HashtagID)}>
                  #{item.Hashtag}
                </Tag>
              ))}
            </Flex>
          )}
        </Flex>
        {getUserStory.isLoading ? (
          <Loading text="Please wait ..." />
        ) : (
          <>
            {getUserStory.data && (
              <>
                <Row gutter={[30, 30]}>
                  {paginatedUserStories.length ? (
                    paginatedUserStories?.map((item, index) => {
                      return (
                        <Col xl={6} lg={8} md={12} key={index}>
                          <StoryCard
                            {...item}
                            onEditTitle={handleCardEditTitle}
                            onSetVisibility={handleCardSetVisibility}
                          />
                        </Col>
                      );
                    })
                  ) : (
                    <EmptyStory />
                  )}
                </Row>

                <div className={styles['my_story_footer']}>
                  {filteredUserStories.length ? (
                    <Pagination
                      defaultCurrent={1}
                      size="default"
                      className={styles['pagination']}
                      total={filteredUserStories.length}
                      defaultPageSize={4}
                      onChange={(page) =>
                        setPaginatedUserStories(
                          paginateData(filteredUserStories, page),
                        )
                      }
                    />
                  ) : (
                    ''
                  )}

                </div>
              </>
            )}
          </>
        )}
      </div>
      <div className={styles['story_container']}>
        <Flex className={styles['story_header']} align="center">
          <h2 className={styles['title']}>My Public Stories</h2>
          {getUserHashtag.data && (
            <Flex
              style={{
                marginLeft: 20,
                maxWidth: 'calc(100% - 150px)',
                overflowX: 'auto',
                paddingBottom: 5,
              }}>
              <Tag
                style={{
                  borderColor: '#8A8181',
                  cursor: 'pointer',
                  color: MyStoryHashtagAvtice === 0 ? '#fff' : '#8A8181',
                  borderRadius: 50,
                  background:
                    MyStoryHashtagAvtice === 0 ? '#8A8181' : 'transparent',
                }}
                onClick={() => handleMyStoryHashtag(0)}>
                #All
              </Tag>
              {getUserHashtag.data.map((item) => (
                <Tag
                  key={item.HashtagID}
                  style={{
                    borderColor: '#8A8181',
                    cursor: 'pointer',
                    color:
                      MyStoryHashtagAvtice === item.HashtagID
                        ? '#fff'
                        : '#8A8181',
                    borderRadius: 50,
                    background:
                      MyStoryHashtagAvtice === item.HashtagID
                        ? '#8A8181'
                        : 'transparent',
                  }}
                  onClick={() => handleMyStoryHashtag(item.HashtagID)}>
                  #{item.Hashtag}
                </Tag>
              ))}
            </Flex>
          )}
        </Flex>
        {getUserStory.isLoading ? (
          <Loading text="Please wait ..." />
        ) : (
          <>
            {getUserStory.data && (
              <>
                <Row gutter={[30, 30]}>
                  {paginatedUserPublicStories.length ? (
                    paginatedUserPublicStories?.map((item, index) => {
                      return (
                        <Col xl={6} lg={8} md={12} key={index}>
                          <StoryCard
                            {...item}
                            onEditTitle={handleCardEditTitle}
                            onSetVisibility={handleCardSetVisibility}
                          />
                        </Col>
                      );
                    })
                  ) : (
                    <EmptyStory />
                  )}
                </Row>

                <div className={styles['my_story_footer']}>
                  {filteredUserStories.length ? (
                    <Pagination
                      defaultCurrent={1}
                      size="default"
                      className={styles['pagination']}
                      total={filteredUserStories.length}
                      defaultPageSize={4}
                      onChange={(page) =>
                        setPaginatedUserStories(
                          paginateData(filteredUserStories, page),
                        )
                      }
                    />
                  ) : (
                    ''
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
      {/* Modals for card actions */}
      {selectedVisibilityStory && (
        <VisibilityModal
          visible={isVisibilityModalVisible}
          isPrivate={selectedVisibilityStory.IsPrivate}
          onConfirm={handleConfirmVisibility}
          onCancel={handleCancelVisibility}
          loading={isUpdatingVisibility}
        />
      )}
      {selectedEditTitleStory && (
        <EditTitleModal
          visible={isEditTitleModalVisible}
          onCancel={handleCancelEditTitle}
          storyData={selectedEditTitleStory}
          onSuccess={handleEditTitleSuccess}
        />
      )}
    </>
  );
};

export default WebLibrary;
